"use client";

import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertCircle,
  ArrowRight,
  Eye,
  EyeOff,
  Loader2,
  Lock,
  Mail,
  User,
} from "lucide-react";
import Link from "next/link";
import { cn } from "@/lib/utils";
import { PASSWORD_MIN_LENGTH } from "@/lib/auth/password";
import { isValidLoginIdentifier } from "@/lib/auth/resolve-login-identifier";
import { OAuthButtons } from "./OAuthButtons";
import type { useAuthForm } from "./useAuthForm";
import { AuthMode } from "./AuthShell";

type AuthFormState = ReturnType<typeof useAuthForm>;

type AuthFormPanelProps = AuthFormState & {
  mode: AuthMode;
  onSwitchMode: (mode: AuthMode) => void;
};

export function AuthFormPanel({
  mode,
  username,
  setUsername,
  email,
  setEmail,
  password,
  setPassword,
  rememberMe,
  setRememberMe,
  error,
  setError,
  isLoading,
  submitCredentials,
  onSwitchMode,
}: AuthFormPanelProps) {
  const isSignUp = mode === "signup";
  const isForgot = mode === "forgot";
  const [showPassword, setShowPassword] = useState(false);
  const minPasswordLen = isSignUp ? PASSWORD_MIN_LENGTH : 1;
  
  const [forgotStatus, setForgotStatus] = useState<"idle" | "loading" | "success">("idle");
  const [forgotError, setForgotError] = useState<string | null>(null);
  const [forgotSuccessMessage, setForgotSuccessMessage] = useState<string | null>(
    null,
  );

  // Reset forgot UI only when leaving forgot mode (not on every login/signup mount).
  useEffect(() => {
    if (!isForgot) return;
    return () => {
      setForgotStatus("idle");
      setForgotError(null);
      setForgotSuccessMessage(null);
    };
  }, [isForgot]);

  const isFormValid =
    isForgot
      ? username.trim().length >= 3 && email.trim().length > 0
      : isSignUp
        ? username.trim().length >= 3 && password.length >= minPasswordLen
        : isValidLoginIdentifier(username) && password.length >= minPasswordLen;

  const handleForgotSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !email.trim() || forgotStatus === "loading") return;
    setForgotError(null);
    setForgotStatus("loading");
    try {
      const res = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: username.trim(),
          email: email.trim(),
        }),
      });
      const json = (await res.json()) as {
        success: boolean;
        error?: string;
        data?: { message?: string };
      };
      if (!res.ok || !json.success) {
        setForgotError(json.error ?? "Could not send reset link");
        setForgotStatus("idle");
        return;
      }
      setForgotSuccessMessage(
        json.data?.message ?? "Password reset link sent. Check your inbox.",
      );
      setForgotStatus("success");
    } catch (err) {
      setForgotError(err instanceof Error ? err.message : "Something went wrong");
      setForgotStatus("idle");
    }
  };

  return (
    <div className="flex w-full max-w-[420px] flex-col">
      <div className="mb-8">
        <h1 className="text-3xl font-semibold tracking-tight text-white">
          {isSignUp ? "Create account" : isForgot ? "Reset password" : "Sign in"}
        </h1>
        <p className="mt-2 text-sm text-neutral-400">
          {isSignUp
            ? "Request access to your interactive notebook workspace."
            : isForgot
            ? "Enter your credentials to receive a reset link."
            : "Sign in using your account or preferred provider."}
        </p>
      </div>

      {forgotStatus === "success" ? (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm text-center"
        >
          <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center">
            <Mail className="h-6 w-6 text-amber-500" />
          </div>
          <h2 className="text-lg font-medium text-neutral-900 mb-2">Check your email</h2>
          <p className="text-sm text-neutral-500 mb-6">
            {forgotSuccessMessage ??
              "Password reset link sent. Check your inbox."}
          </p>
          <button
            onClick={() => {
              setForgotStatus("idle");
              setForgotError(null);
              setForgotSuccessMessage(null);
              onSwitchMode("login");
            }}
            className="text-sm font-medium text-neutral-900 hover:underline"
          >
            Back to login
          </button>
        </motion.div>
      ) : (
      <form onSubmit={isForgot ? handleForgotSubmit : submitCredentials} className="space-y-4">
        <label className="block space-y-1.5">
          <span className="sr-only">
            {isSignUp || isForgot ? "Username" : "Username or email"}
          </span>
          <div className="relative">
            {isSignUp || isForgot ? (
              <User className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500" />
            ) : (
              <Mail className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500" />
            )}
            <input
              type="text"
              inputMode={isSignUp || isForgot ? "text" : "email"}
              value={username}
              onChange={(e) => {
                setUsername(
                  isSignUp || isForgot
                    ? e.target.value
                        .toLowerCase()
                        .replace(/[^a-z0-9_]/g, "")
                    : e.target.value.trimStart(),
                );
                setError("");
              }}
              placeholder={isSignUp || isForgot ? "Username" : "Username or email"}
              autoComplete="username"
              className="w-full rounded-xl border border-neutral-800 bg-[#0a0a0a] py-3.5 pl-10 pr-4 text-sm text-white placeholder:text-neutral-600 focus:border-neutral-600 focus:outline-none focus:ring-1 focus:ring-neutral-600 shadow-sm"
            />
          </div>
        </label>

        {(isSignUp || isForgot) && (
          <label className="block space-y-1.5">
            <span className="sr-only">Email</span>
            <div className="relative">
              <Mail className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500" />
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError("");
                }}
                placeholder={
                  isForgot ? "Email" : "Recovery email (recommended)"
                }
                autoComplete="email"
                className="w-full rounded-xl border border-neutral-800 bg-[#0a0a0a] py-3.5 pl-10 pr-4 text-sm text-white placeholder:text-neutral-600 focus:border-neutral-600 focus:outline-none focus:ring-1 focus:ring-neutral-600 shadow-sm"
              />
            </div>
          </label>
        )}

        {!isForgot && (
        <label className="block space-y-1.5">
          <span className="sr-only">Password</span>
          <div className="relative">
            <Lock className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500" />
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError("");
              }}
              placeholder={
                isSignUp
                  ? `Password (min ${PASSWORD_MIN_LENGTH} characters)`
                  : "Password"
              }
              minLength={isSignUp ? PASSWORD_MIN_LENGTH : 1}
              autoComplete={isSignUp ? "new-password" : "current-password"}
              className="w-full rounded-xl border border-neutral-800 bg-[#0a0a0a] py-3.5 pl-10 pr-11 text-sm text-white placeholder:text-neutral-600 focus:border-neutral-600 focus:outline-none focus:ring-1 focus:ring-neutral-600 shadow-sm"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-neutral-300"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          </div>
        </label>
        )}

        {(!isSignUp && !isForgot) && (
          <div className="flex items-center justify-between gap-4 pt-1">
            <label className="flex cursor-pointer items-center gap-2.5 text-xs font-medium text-neutral-600 group">
              <button
                type="button"
                role="checkbox"
                aria-checked={rememberMe}
                onClick={() => setRememberMe(!rememberMe)}
                className={cn(
                  "relative flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border transition-all duration-200",
                  rememberMe
                    ? "border-white bg-white shadow-sm"
                    : "border-neutral-700 bg-transparent group-hover:border-neutral-500 shadow-sm"
                )}
              >
                <motion.svg
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={
                    rememberMe
                      ? { scale: 1, opacity: 1 }
                      : { scale: 0.5, opacity: 0 }
                  }
                  transition={{ type: "spring", stiffness: 350, damping: 25 }}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-2.5 w-2.5 text-black"
                >
                  <polyline points="20 6 9 17 4 12" />
                </motion.svg>
              </button>
              Remember me
            </label>
            {/*
              Was: <button onClick={() => onSwitchMode("forgot")}>.
              That only faked the URL via history.pushState while this same
              AuthShell panel swapped to its own "forgot" mode in place — so
              a user who then refreshed got a DIFFERENT page: the real
              Next.js route at /auth/forgot-password renders
              app/account/ForgotPasswordPage.tsx, not this panel's forgot
              mode. Two different UIs for the same URL depending on how you
              arrived. Left commented, not deleted, in case this in-place
              mode is wanted again later — but it must not be the live path,
              since app/account/ForgotPasswordPage.tsx is the one real route.
            */}
            <Link
              href="/auth/forgot-password"
              className="text-xs text-neutral-500 transition hover:text-white"
            >
              Forgot password?
            </Link>
          </div>
        )}

        <AnimatePresence mode="wait">
          {(error || forgotError) && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              className="flex items-center gap-2 rounded-lg border border-red-900/50 bg-red-950/30 px-3 py-2.5 text-xs text-red-400"
            >
              <AlertCircle className="h-3.5 w-3.5 shrink-0" />
              <span>{error || forgotError}</span>
            </motion.div>
          )}
        </AnimatePresence>

        <button
          type="submit"
          disabled={!isFormValid || isLoading || forgotStatus === "loading"}
          className={cn(
            "flex w-full items-center justify-center gap-2 rounded-xl py-3.5 text-sm font-medium transition",
            "bg-white text-black hover:bg-neutral-200 shadow-sm",
            "disabled:cursor-not-allowed disabled:opacity-50",
          )}
        >
          {isLoading || forgotStatus === "loading" ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              <span>{isSignUp ? "Create account" : isForgot ? "Send reset link" : "Sign in"}</span>
              <ArrowRight className="h-4 w-4" />
            </>
          )}
        </button>
      </form>
      )}

      <div className="my-6 flex items-center gap-3">
        <div className="h-px flex-1 bg-neutral-800" />
        <span className="text-[11px] uppercase tracking-wider text-neutral-600">
          Or
        </span>
        <div className="h-px flex-1 bg-neutral-800" />
      </div>

      <OAuthButtons />

      <p className="mt-8 text-center text-xs text-neutral-500">
        {(isSignUp || isForgot) ? (
          <>
            Already have access?&nbsp;
            <a href="/login" onClick={(e) => { e.preventDefault(); onSwitchMode("login"); }} className="text-white font-medium hover:underline">
              Sign in
            </a>
          </>
        ) : (
          <>
            Don&apos;t have an account?&nbsp;
            <a href="/signup" onClick={(e) => { e.preventDefault(); onSwitchMode("signup"); }} className="text-white font-medium hover:underline">
              Sign up
            </a>
          </>
        )}
      </p>
    </div>
  );
}
